import React, { useState, Component, type ReactNode, type ErrorInfo } from 'react';
import {
  Bot,
  History,
  ListTodo,
  MessageCircle,
  Globe,
  ArrowLeftFromLine,
  ArrowRightFromLine,
  X,
  ArrowLeft,
  Shield,
  GitBranch,
  UserPlus,
  ListOrdered,
  Download,
  Pencil,
  Check,
  Menu,
  Settings,
  GitCommit,
  Workflow,
  FileDown,
  Github,
  CheckCircle2,
  Palette,
  Database,
  Clock,
  Activity,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Selectable } from 'kysely';
import { toast } from 'sonner';
import { SessionFiles, SessionStatus } from '../helpers/schema';
import { AnnotationItem } from '../endpoints/annotations/list_GET.schema';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './Tabs';
import { Button } from './Button';
import { Badge } from './Badge';
import { Input } from './Input';
import { InviteGuestDialog } from './InviteGuestDialog';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { AiChatPanel } from './AiChatPanel';
import type { InstalledSkill } from './SkillSearchModal';
import { useUpdateTask } from '../helpers/useTasksApi';
import { useUpdateSession } from '../helpers/useSessionsApi';
import { CheckpointsPanel } from './CheckpointsPanel';
import { TodoListPanel } from './TodoListPanel';
import { CollaboratorChat } from './CollaboratorChat';
import { CombinedChatPanel } from './CombinedChatPanel';
import { AiActivityPanel } from './AiActivityPanel';
import { TaskQueuePanel } from './TaskQueuePanel';
import { PublishedSitesPanel } from './PublishedSitesPanel';
import { ChatToolbar } from './ChatToolbar';
import { N8nPanel } from './N8nPanel';
import { GitPanel } from './GitPanel';
import { GitTreePanel } from './GitTreePanel';
import { SessionSettingsPanel } from './SessionSettingsPanel';
import { ThemesPanel } from './ThemesPanel';
import { DatabasePanel } from './DatabasePanel';
import { TimeTrackerPanel } from './TimeTrackerPanel';
import { useUnreadChatCount } from '../helpers/useChatApi';
import { getTemplateConfig } from '../helpers/templateConfig';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './DropdownMenu';
import styles from './SessionRightPanel.module.css';

class PanelErrorBoundary extends Component<
  { name: string; children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[PanelErrorBoundary:${this.props.name}]`, error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '1rem', color: 'var(--destructive)', fontSize: '0.85rem' }}>
          <p style={{ fontWeight: 600 }}>Panel crashed: {this.props.name}</p>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem', fontSize: '0.75rem' }}>
            {this.state.error?.message}
            {'\n'}
            {this.state.error?.stack}
          </pre>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginTop: '0.5rem', padding: '0.25rem 0.75rem', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export type RightTab =
  | 'ai'
  | 'activity'
  | 'checkpoints'
  | 'todos'
  | 'chat'
  | 'queue'
  | 'sites'
  | 'database'
  | 'themes'
  | 'git'
  | 'time'
  | 'settings';
export type PanelSide = 'left' | 'right';

interface SessionRightPanelProps {
  activeTab: RightTab;
  onTabChange: (tab: RightTab) => void;
  sessionId: string;
  sessionName: string;
  sessionStatus: SessionStatus;
  templateId?: string | null;
  activeFile: Selectable<SessionFiles> | undefined;
  sessionFiles?: Selectable<SessionFiles>[];
  annotations: AnnotationItem[];
  isOwner?: boolean;
  isFavorited: boolean;
  onToggleFavorite: () => void;
  onInsertCode: (code: string) => void;
  onPreviewCheckpoint: (id: string) => void;
  onOpenPermissions: () => void;
  onOpenImport?: () => void;
  onOpenAnnotation?: () => void;
  onRequestAnnotateAttachment?: (dataUrl: string) => void;
  panelSide: PanelSide;
  onToggleSide: () => void;
  onClose: () => void;
  initialAttachment?: { content: string; name?: string } | null;
  onAttachmentSent?: () => void;
  autoExecute?: boolean;
  onAutoExecuteChange?: (enabled: boolean) => void;
  githubConnected?: boolean;
  onGithubConnect?: () => void;
  onSessionNameChange?: (newName: string) => void;
  onSendMessageRef?: (sendFn: (message: string) => void) => void;
  onOpenSkillSearch?: (query?: string) => void;
  onOpenSkillTree?: (mode?: 'view' | 'create') => void;
  activeSkills?: InstalledSkill[];
  onActiveSkillsChange?: (skills: InstalledSkill[]) => void;
  onRequestScreenshot?: () => void;
  previewScreenshot?: string | null;
}

export const SessionRightPanel: React.FC<SessionRightPanelProps> = ({
  activeTab,
  onTabChange,
  sessionId,
  sessionName,
  sessionStatus,
  templateId,
  activeFile,
  annotations,
  isOwner,
  isFavorited,
  onToggleFavorite,
  onInsertCode,
  onPreviewCheckpoint,
  onOpenPermissions,
  onOpenImport,
  onOpenAnnotation,
  onRequestAnnotateAttachment,
  panelSide,
  onToggleSide,
  onClose,
  initialAttachment,
  onAttachmentSent,
  sessionFiles,
  autoExecute,
  onAutoExecuteChange,
  githubConnected = false,
  onGithubConnect,
  onSessionNameChange,
  onSendMessageRef,
  onOpenSkillSearch,
  onOpenSkillTree,
  activeSkills,
  onActiveSkillsChange,
  onRequestScreenshot,
  previewScreenshot,
}) => {
  const navigate = useNavigate();
  const urlParams =
    typeof window !== 'undefined' ? new URL(window.location.href).searchParams : null;
  const debugShowDownload = urlParams?.get('debugShowDownload') === '1';

  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);
  const [isN8nPanelOpen, setIsN8nPanelOpen] = React.useState(false);
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [editedName, setEditedName] = React.useState(sessionName);
  const nameInputRef = React.useRef<HTMLInputElement>(null);
  const aiChatSendRef = React.useRef<((message: string) => void) | null>(null);

  const updateMutation = useUpdateTask();
  const updateSessionMutation = useUpdateSession();

  // Mobile tab options
  const mobileTabs = [
    { id: 'ai', icon: Bot, label: 'AI Chat' },
    { id: 'activity', icon: Activity, label: 'AI Activity' },
    { id: 'checkpoints', icon: History, label: 'History' },
    { id: 'todos', icon: ListTodo, label: 'Todos' },
    { id: 'queue', icon: ListOrdered, label: 'Queue' },
    { id: 'chat', icon: MessageCircle, label: 'Team Chat' },
    { id: 'git', icon: GitCommit, label: 'Git' },
    { id: 'sites', icon: Globe, label: 'Sites' },
    { id: 'database', icon: Database, label: 'Database' },
    { id: 'themes', icon: Palette, label: 'Themes' },
    { id: 'time', icon: Clock, label: 'Time Tracker' },
    { id: 'settings', icon: Settings, label: 'Session Settings' },
  ];

  // Get template configuration for display
  const templateConfig = getTemplateConfig(templateId);

  // Update edited name when sessionName prop changes
  React.useEffect(() => {
    setEditedName(sessionName);
  }, [sessionName]);

  // Focus input when editing starts
  React.useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // Listen for invite event from SessionNavToolbar
  React.useEffect(() => {
    const handler = () => {
      if (isOwner) setInviteDialogOpen(true);
    };
    window.addEventListener('tasking.openInvite', handler);
    return () => window.removeEventListener('tasking.openInvite', handler);
  }, [isOwner]);

  const handleSaveName = async () => {
    const trimmedName = editedName.trim();
    if (!trimmedName) {
      setEditedName(sessionName);
      setIsEditingName(false);
      return;
    }
    if (trimmedName === sessionName) {
      setIsEditingName(false);
      return;
    }
    try {
      await updateSessionMutation.mutateAsync({ id: sessionId, name: trimmedName });
      onSessionNameChange?.(trimmedName);
      toast.success('Session name updated');
      setIsEditingName(false);
    } catch (err) {
      toast.error('Failed to update session name');
      setEditedName(sessionName);
      setIsEditingName(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      setEditedName(sessionName);
      setIsEditingName(false);
    }
  };

  // Track unread chat messages
  const { unreadCount: chatUnreadCount, markAsRead: markChatAsRead } =
    useUnreadChatCount(sessionId);

  // Mark messages as read when chat tab is activated
  React.useEffect(() => {
    if (activeTab === 'chat') {
      markChatAsRead();
    }
  }, [activeTab, markChatAsRead]);

  const handleExecuteTask = React.useCallback(
    (taskId: string, taskTitle: string, taskDescription?: string) => {
      // Optimistically mark task in_progress
      try {
        updateMutation.mutate({ taskId, status: 'in_progress' });
      } catch (e) {
        // ignore
      }

      // Switch to AI tab
      onTabChange('ai');
      // Send message to AI chat after a brief delay to ensure tab is switched
      setTimeout(() => {
        const message = taskDescription
          ? `Please implement: ${taskTitle}\n\nDescription: ${taskDescription}`
          : `Please implement: ${taskTitle}`;
        if (aiChatSendRef.current) {
          aiChatSendRef.current(message);
        }
      }, 100);
    },
    [onTabChange, updateMutation],
  );

  // Execute a suggestion from a completed todo by sending a targeted prompt to AI chat
  const handleExecuteSuggestion = React.useCallback(
    (suggestion: string) => {
      onTabChange('ai');
      setTimeout(() => {
        const message = `Implement this suggestion in the project: ${suggestion}\nReturn ONLY code blocks with filename=... for any files to create or modify.`;
        if (aiChatSendRef.current) {
          aiChatSendRef.current(message);
          try {
            // show feedback to user
            toast.success('Suggestion sent to AI chat');
          } catch (e) {
            // ignore if toast not available
          }
        }
      }, 100);
    },
    [onTabChange],
  );

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        {/* Chat Toolbar */}
        <ChatToolbar
          activeTab={activeTab}
          onTabChange={onTabChange as (tab: string) => void}
          onFavorite={onToggleFavorite}
          isFavorited={isFavorited}
          onDownload={
            isOwner || debugShowDownload
              ? () => {
                  window.location.href = `/_api/sessions/download?sessionId=${sessionId}`;
                }
              : undefined
          }
          onImport={onOpenImport}
          onPermissions={onOpenPermissions}
          onAnnotate={onOpenAnnotation}
          showDownload={isOwner || debugShowDownload}
          showImport={!!onOpenImport}
          showPermissions={true}
          className={styles.chatToolbar}
          githubConnected={githubConnected}
          onGithubConnect={onGithubConnect}
          panelSide={panelSide}
          onToggleSide={onToggleSide}
          isOwner={isOwner}
          chatUnreadCount={chatUnreadCount}
        />

        {/* n8n Workflows Panel */}
        {isN8nPanelOpen && <N8nPanel open={isN8nPanelOpen} onOpenChange={setIsN8nPanelOpen} />}

        <div className={styles.panelContent}>
          {/* Session Header */}
          <div className={styles.sessionHeader}>
            <div className={styles.sessionHeaderLeft}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => navigate('/dashboard')}
                    className={styles.backButton}
                  >
                    <ArrowLeft size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Back to dashboard</TooltipContent>
              </Tooltip>

              <div className={styles.sessionInfo}>
                {/* Session title row with framework tag, name, and status */}
                <div className={styles.sessionTitleRow}>
                  {/* Framework Tag - in front of name */}
                  {templateConfig && (
                    <span
                      className={styles.frameworkTag}
                      style={{
                        color: templateConfig.color,
                        backgroundColor: templateConfig.bgColor,
                        borderColor: templateConfig.borderColor,
                      }}
                    >
                      <span className={styles.frameworkIcon}>{templateConfig.icon}</span>
                      {templateConfig.name}
                    </span>
                  )}

                  {/* Editable Session Name */}
                  {isEditingName ? (
                    <div className={styles.nameEditContainer}>
                      <Input
                        ref={nameInputRef}
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={handleSaveName}
                        className={styles.nameInput}
                      />
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={handleSaveName}
                        className={styles.saveNameButton}
                      >
                        <Check size={14} />
                      </Button>
                    </div>
                  ) : (
                    <div className={styles.nameDisplayContainer}>
                      <h2 className={styles.sessionName}>{sessionName}</h2>
                      {isOwner && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => setIsEditingName(true)}
                              className={styles.editNameButton}
                            >
                              <Pencil size={12} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit name</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  <Badge
                    variant={sessionStatus === 'active' ? 'success' : 'secondary'}
                    className={styles.statusBadge}
                  >
                    {sessionStatus}
                  </Badge>
                </div>
              </div>
            </div>

            <div className={styles.sessionHeaderRight}>
              {/* Mobile Menu - Tab Switcher (CSS controls visibility) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" className={styles.mobileMenuButton}>
                    <Menu size={18} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={styles.mobileMenuContent}>
                  {mobileTabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <DropdownMenuItem
                        key={tab.id}
                        onClick={() => onTabChange(tab.id as RightTab)}
                        className={isActive ? styles.mobileMenuItemActive : ''}
                      >
                        <Icon size={16} />
                        <span>{tab.label}</span>
                        {tab.id === 'chat' && chatUnreadCount > 0 && (
                          <Badge variant="destructive" className={styles.menuBadge}>
                            {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                          </Badge>
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  {isOwner && onOpenPermissions && (
                    <DropdownMenuItem onClick={onOpenPermissions}>
                      <Shield size={16} />
                      <span>Permissions</span>
                    </DropdownMenuItem>
                  )}
                  {isOwner && (
                    <DropdownMenuItem onClick={() => setInviteDialogOpen(true)}>
                      <UserPlus size={16} />
                      <span>Invite</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/workflows')}>
                    <Workflow size={16} />
                    <span>Workflows</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/taskingbot')}>
                    <Bot size={16} />
                    <span>TaskingBot</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(v) => onTabChange(v as RightTab)}
            className={styles.tabs}
          >
            {/* Tabs are now controlled by the ChatToolbar */}

            <TabsContent value="ai" className={styles.tabContent}>
              {activeTab === 'ai' && (
                <PanelErrorBoundary name="AiChatPanel">
                  <AiChatPanel
                    sessionId={sessionId}
                    isOwner={isOwner}
                    currentFile={activeFile?.filePath}
                    className={styles.panelContent}
                    annotations={annotations}
                    initialAttachment={initialAttachment}
                    onAttachmentSent={onAttachmentSent}
                    onRequestAnnotateAttachment={onRequestAnnotateAttachment}
                    autoExecute={autoExecute}
                    onAutoExecuteChange={onAutoExecuteChange}
                    onSendMessageRef={(sendFn) => {
                      aiChatSendRef.current = sendFn;
                      // Also expose to parent for auto-generation
                      if (onSendMessageRef) {
                        onSendMessageRef(sendFn);
                      }
                    }}
                    onOpenSkillSearch={onOpenSkillSearch}
                    onOpenSkillTree={onOpenSkillTree}
                    activeSkills={activeSkills}
                    onActiveSkillsChange={onActiveSkillsChange}
                    onRequestScreenshot={onRequestScreenshot}
                    previewScreenshot={previewScreenshot}
                  />
                </PanelErrorBoundary>
              )}
            </TabsContent>
            <TabsContent value="activity" className={styles.tabContent}>
              {activeTab === 'activity' && (
                <AiActivityPanel sessionId={sessionId} className={styles.panelContent} />
              )}
            </TabsContent>
            <TabsContent value="checkpoints" className={styles.tabContent}>
              {activeTab === 'checkpoints' && (
                <CheckpointsPanel
                  sessionId={sessionId}
                  className={styles.panelContent}
                  onPreviewCheckpoint={onPreviewCheckpoint}
                  currentFiles={sessionFiles}
                />
              )}
            </TabsContent>
            <TabsContent value="todos" className={styles.tabContent}>
              <TodoListPanel
                sessionId={sessionId}
                className={styles.panelContent}
                onExecuteTask={handleExecuteTask}
                onExecuteSuggestion={handleExecuteSuggestion}
              />
            </TabsContent>
            <TabsContent value="queue" className={styles.tabContent}>
              {activeTab === 'queue' && (
                <TaskQueuePanel
                  sessionId={sessionId}
                  files={sessionFiles}
                  className={styles.panelContent}
                  onExecuteTask={handleExecuteTask}
                />
              )}
            </TabsContent>
            <TabsContent value="chat" className={styles.tabContent}>
              {activeTab === 'chat' && (
                <PanelErrorBoundary name="CombinedChatPanel">
                  <CombinedChatPanel
                    sessionId={sessionId}
                    className={styles.panelContent}
                    isOwner={isOwner}
                    currentFile={activeFile?.filePath}
                    onOpenCoding={() => onTabChange('ai')}
                    onOpenSkillSearch={onOpenSkillSearch}
                    onOpenSkillTree={onOpenSkillTree}
                    activeSkills={activeSkills}
                  />
                </PanelErrorBoundary>
              )}
            </TabsContent>
            <TabsContent value="sites" className={styles.tabContent}>
              {activeTab === 'sites' && <PublishedSitesPanel sessionId={sessionId} />}
            </TabsContent>
            <TabsContent value="database" className={styles.tabContent}>
              {activeTab === 'database' && <DatabasePanel sessionId={sessionId} isOwner={isOwner} />}
            </TabsContent>
            <TabsContent value="themes" className={styles.tabContent}>
              {activeTab === 'themes' && <ThemesPanel sessionId={sessionId} isOwner={isOwner} />}
            </TabsContent>
            <TabsContent value="git" className={styles.tabContent}>
              {activeTab === 'git' && (
                <GitTreePanel
                  sessionId={sessionId}
                  files={(sessionFiles || []).map((f) => ({
                    id: f.id,
                    fileName: f.fileName,
                    filePath: f.filePath,
                    content: f.content || '',
                  }))}
                />
              )}
            </TabsContent>
            <TabsContent value="time" className={styles.tabContent}>
              {activeTab === 'time' && (
                <PanelErrorBoundary name="TimeTrackerPanel">
                  <TimeTrackerPanel sessionId={sessionId} />
                </PanelErrorBoundary>
              )}
            </TabsContent>
            <TabsContent value="settings" className={styles.tabContent}>
              {activeTab === 'settings' && <SessionSettingsPanel sessionId={sessionId} isOwner={isOwner} />}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {isOwner && (
        <InviteGuestDialog
          sessionId={sessionId}
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
        />
      )}
    </div>
  );
};
